import { marked } from 'marked'
import DOMPurify from 'dompurify'

// Configure marked for safe, readable output
marked.setOptions({
  gfm: true,
  breaks: true,
})

/**
 * Untrusted-input boundary marker delimiters (#751 / ADR-031). Job
 * descriptions, plan step text, and step titles can carry operator/user/prior-
 * bead text that a bead runtime already wrapped in boundary markers such as
 * `<<<OPERATOR_PROVIDED_TEXT_START>>>...<<<OPERATOR_PROVIDED_TEXT_END>>>` before
 * handing it to an LLM. Those delimiters are structural for the LLM's context,
 * not operator-facing content. `marked.parse()` + `DOMPurify` treat `<<<NAME>>>`
 * as an unrecognized HTML tag and strip the tag, leaving the bare `<<`/`>>`
 * visible — so this removes the whole delimiter token before markdown rendering,
 * leaving the wrapped text intact.
 *
 * SOURCE OF TRUTH: the real marker CLASS NAMES live in
 * `shared/src/untrusted-input-markers.ts` (UNTRUSTED_USER_TEXT_*,
 * OPERATOR_PROVIDED_TEXT_*, PRIOR_BEAD_OUTPUT_*). They are mirrored here — not
 * imported — because 86proof-site's webpack build has no `@beads/shared` build
 * path (no TS loader; the package declares zero cross-package deps and
 * `@beads/shared` resolves to a prebuilt `dist/` nothing in the console build
 * produces). Keep this list in sync with that module if a marker class is added
 * or renamed.
 *
 * Why an allowlist and NOT a broad `[A-Z_]+`: a broad `[A-Z_]+_(?:START|END)`
 * pattern would also match legitimate, marker-SHAPED prose/code such as
 * `<<<SQL_START>>>` and silently delete it — a No-Data-Loss violation.
 * Restricting to the three real class names means only actual boundary markers
 * are stripped; unrelated `<<<...>>>` text is left for normal rendering.
 */
const MARKER_CLASS = '(?:UNTRUSTED_USER_TEXT|OPERATOR_PROVIDED_TEXT|PRIOR_BEAD_OUTPUT)'

// Opener alternation:
//  - `<<<` — the ASCII opener the wrapper emits.
//  - `«««` (three U+00AB) — the NEUTRALIZED opener written by
//    `neutralizeMarkers()` in the shared module when it defangs a `<<<` that
//    appeared inside wrapped content. A run of >3 open-lookalike chars leaves a
//    residual lookalike between the `«««` and the class name (e.g. `<<<<X_END>>>`
//    -> `«««<X_END>>>`), so the neutralized branch tolerates trailing
//    open-lookalike / zero-width chars before the class name.
// `«` is a UTF-8 literal, matching how the shared source writes it; this file is
// UTF-8 and read as UTF-8 by both the vitest and webpack (babel) toolchains.
const MARKER_TOKEN_SOURCE =
  `(?:<<<|«««[<\\uFF1C\\uFE64\\u200B\\u200C\\u200D\\u2060\\uFEFF]*)${MARKER_CLASS}_(?:START|END)>>>`

// A marker token embedded inline within a line of text.
const MARKER_TOKEN_PATTERN = new RegExp(MARKER_TOKEN_SOURCE, 'g')
// A marker token alone on its own line (optionally padded by horizontal
// whitespace) — matched WITH its own line's terminator so removing it does not
// disturb the indentation of the real content on the next line. The trailing
// `(?:\r?\n|$)` REQUIRES an actual line end or EOF after the marker + trailing
// spaces, so `<<<...START>>>    const x = 1` (marker + content on ONE line) is
// NOT treated as a standalone line (which would eat the content's leading
// indent) — it falls through to inline removal, which strips only the token.
const STANDALONE_MARKER_LINE_PATTERN = new RegExp(
  `^[^\\S\\r\\n]*${MARKER_TOKEN_SOURCE}[^\\S\\r\\n]*(?:\\r?\\n|$)`,
  'gm',
)

/**
 * Strip untrusted-input boundary marker delimiter tokens from text. Only the
 * marker tokens are removed; the wrapped content — including its leading
 * indentation — is left byte-for-byte intact.
 *
 * A marker alone on its line is removed together with that line's terminator,
 * so a following indented code block keeps its indentation. A marker embedded
 * inline in a text line is removed in place. NO whole-string trim or blank-line
 * collapse is applied: doing so would eat the leading indentation of content
 * that immediately follows a removed START marker (demoting an indented code
 * block to a broken paragraph). `renderMarkdown()`'s existing line-ending
 * normalization + `marked` handle any surrounding whitespace.
 * @param {string} text
 * @returns {string}
 */
function stripMarkerDelimiters(text) {
  return text
    .replace(STANDALONE_MARKER_LINE_PATTERN, '')
    .replace(MARKER_TOKEN_PATTERN, '')
}

/**
 * Render markdown string to HTML.
 * Preprocesses text to restore structure that may have been flattened.
 * @param {string} text - Raw markdown text
 * @returns {string} - Rendered HTML
 */
export function renderMarkdown(text) {
  if (!text || typeof text !== 'string') return ''

  // Preprocess to restore structure that was flattened (newlines stripped)
  let processed = text
    // Normalize line endings
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')

  // Strip untrusted-input boundary markers (#751) before any further
  // preprocessing, so downstream heuristics see only real content.
  processed = stripMarkerDelimiters(processed)

  // Only apply structure restoration if text appears flattened
  // (long text with no newlines that has list/section markers)
  const hasNewlines = processed.includes('\n')
  const looksFlattened = !hasNewlines && processed.length > 200 &&
    (/\d+\.\s+[A-Z]/.test(processed) || /\s-\s+[A-Z]/.test(processed))

  if (looksFlattened) {
    processed = processed
      // Section labels followed by content - put on own line and bold
      // "Steps: 1." or "Context: The" -> "\n\n**Steps:**\n1." or "\n\n**Context:**\nThe"
      .replace(/(^|[.!?])\s*(Steps|Context|Constraints|Changes|Notes|Summary|Description|Requirements|Acceptance|Modified|New file):\s*/gi,
        '$1\n\n**$2:**\n')

      // Numbered items: ". 1. Text" or ": 1. Text" -> newline before number
      .replace(/([.!?:,])\s+(\d+)\.\s+/g, '$1\n$2. ')

      // Bullet points after sentence end or colon
      .replace(/([.!?:])\s+-\s+/g, '$1\n- ')

      // Bullet points mid-text (before capital letter, likely a list item)
      .replace(/([a-z])\s+-\s+([A-Z])/g, '$1\n- $2')

      // Clean up excessive newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }

  const raw = marked.parse(processed)
  return DOMPurify.sanitize(raw)
}
